import { DEFAULT_TIMEZONE, StatusEnum } from 'components/Chargeback/constants';
import { GREATER_MSG, LESS_MSG, NAME_ENUMS } from 'constants/products';
import { set } from 'lodash';

export const sumTaxesAndFees = (data) => {
  let sum = 0;
  if (
    data.charges?.servicingFee &&
    Object.keys(data.charges?.servicingFee).length
  ) {
    let marketplaceFee = data?.charges?.servicingFee?.marketplaceFee || 0;
    let ordersCoFee = data?.charges?.servicingFee?.ordersCoFee || 0;
    let processingFee = data?.charges?.servicingFee?.processingFee || 0;
    sum = Number(marketplaceFee) + Number(ordersCoFee) + Number(processingFee);
  }
  return sum;
};

const MAX_ALLOWED_NUMBER = 100000;

const isFilledNumber = (value) => {
  if (value === '' || value === null || value === undefined) {
    return false;
  }
  return !Number.isNaN(Number(value));
};

const isComparableBound = (value) => isFilledNumber(value) && Number(value) > 0;

export const validationModifierCustomersCan = (
  formState,
  copyFormErrors,
  formErrors,
  updateFormErrors,
  name
) => {
  const minT = Number(formState.minTotalNumberOfModifierItems);
  const maxT = Number(formState.maxTotalNumberOfModifierItems);
  const minAnySingle = Number(formState.minAnySingleModifierItems);
  const maxAnySingle = Number(formState.maxAnySingleModifierItems);
  const free = Number(formState.free);
  const min = Number(formState.min);
  const max = Number(formState.max);
  let nextErrors = { ...formErrors, ...copyFormErrors };

  const setFieldError = (field, error, message) => {
    nextErrors = set(nextErrors, field, { error, message });
  };

  const applyRangeErrors = ({
    minValue,
    maxValue,
    minKey,
    maxKey,
    minFilled,
    maxFilled,
    editedField,
  }) => {
    const rangeInvalid = minFilled && maxFilled && minValue > maxValue;

    if (editedField === minKey) {
      if (minValue > MAX_ALLOWED_NUMBER) {
        setFieldError(minKey, true, 'menu.max_number');
      } else if (rangeInvalid) {
        setFieldError(minKey, true, `${GREATER_MSG} ${maxValue}`);
        setFieldError(maxKey, true, `${LESS_MSG} ${minValue}`);
      } else {
        setFieldError(minKey, false, '');
        if (maxFilled) {
          setFieldError(maxKey, false, '');
        }
      }
      return;
    }

    if (maxValue > MAX_ALLOWED_NUMBER) {
      setFieldError(maxKey, true, 'menu.max_number');
      return;
    }

    if (rangeInvalid) {
      setFieldError(maxKey, true, `${LESS_MSG} ${minValue}`);
      setFieldError(minKey, true, `${GREATER_MSG} ${maxValue}`);
      return;
    }

    setFieldError(maxKey, false, '');
    if (minFilled) {
      setFieldError(minKey, false, '');
    }
  };

  switch (name) {
    case NAME_ENUMS.min:
    case NAME_ENUMS.max:
      applyRangeErrors({
        minValue: min,
        maxValue: max,
        minKey: NAME_ENUMS.min,
        maxKey: NAME_ENUMS.max,
        minFilled: isFilledNumber(formState.min),
        maxFilled: isComparableBound(formState.max),
        editedField: name,
      });
      break;
    case NAME_ENUMS.minT:
    case NAME_ENUMS.maxT:
      applyRangeErrors({
        minValue: minT,
        maxValue: maxT,
        minKey: NAME_ENUMS.minT,
        maxKey: NAME_ENUMS.maxT,
        minFilled: isFilledNumber(formState.minTotalNumberOfModifierItems),
        maxFilled: isComparableBound(formState.maxTotalNumberOfModifierItems),
        editedField: name,
      });
      break;
    case NAME_ENUMS.minAnySingle:
    case NAME_ENUMS.maxAnySingle:
      applyRangeErrors({
        minValue: minAnySingle,
        maxValue: maxAnySingle,
        minKey: NAME_ENUMS.minAnySingle,
        maxKey: NAME_ENUMS.maxAnySingle,
        minFilled: isFilledNumber(formState.minAnySingleModifierItems),
        maxFilled: isComparableBound(formState.maxAnySingleModifierItems),
        editedField: name,
      });
      if (
        name === NAME_ENUMS.maxAnySingle &&
        !isComparableBound(formState.maxAnySingleModifierItems)
      ) {
        setFieldError(name, true, 'menu.min_number');
      }
      break;
    case NAME_ENUMS.free:
      if (free > MAX_ALLOWED_NUMBER) {
        setFieldError(name, true, 'menu.max_number');
      } else {
        setFieldError(name, false, '');
      }
      break;
    default:
      break;
  }
  updateFormErrors({ ...nextErrors });
};

export const sortableProvidersByStatus = (arr) => {
  return arr.sort((a, b) => {
    if (a.status < b.status) {
      return 1;
    }
    if (a.status > b.status) {
      return -1;
    }
    return 0;
  });
};

export const groupByCategory = (data) => {
  let sortIndex = 0;
  let groupName = null;
  return data?.reduce((acc, item) => {
    // const ids = item._id || item.externalId || item.internalId || uuidv4();
    if (item?.externalId === '__INFO_LINE__') {
      groupName = item?.name;
    }
    if (item?.category) {
      let categoryId = item.category.name;
      if (groupName) {
        categoryId = `${groupName}__${categoryId}`;
      }
      if (!acc[categoryId]) {
        acc[categoryId] = {
          items: [],
          categoryName: item.category.name,
        };
      }
      acc[categoryId].items.push(item);
    } else {
      let ids = sortIndex;
      if (groupName) {
        ids = `${groupName}__${sortIndex}`;
      }
      if (!acc[ids]) {
        acc[ids] = {
          items: [],
        };
      }
      acc[ids].items.push(item);
      sortIndex++;
    }

    return acc;
  }, []);
};

export function getOrdinalSuffix(day) {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

export const formatDateByStatus = (dateString, type) => {
  const currentBusiness =
    dateString?.orderData && dateString?.orderData?.business[0];
  const timeZoneId = currentBusiness?.timezone?.timeZoneId || DEFAULT_TIMEZONE;
  const dateToUse =
    type === StatusEnum.CREATED
      ? dateString?.requestDate || dateString?.createdAt
      : dateString?.status?.updatedAt;
  const date = new Date(dateToUse);

  const timeOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timeZoneId,
  };
  const dateOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: timeZoneId,
  };

  const timePart = new Intl.DateTimeFormat('en-US', timeOptions).format(date);
  const datePart = new Intl.DateTimeFormat('en-US', dateOptions).format(date);

  const day = date.getDate();
  const ordinalSuffix = getOrdinalSuffix(day);

  return `${timePart} ${datePart}${ordinalSuffix}` || '';
};

export const capitalizedWord = (word) => {
  const BRAND = {
    visa: 'VISA',
    mastercard: 'Master Card',
    american_express: 'American Express',
    amex: 'American Express',
    discover: 'Discover',
  };

  if (BRAND[word]) {
    return BRAND[word];
  }
  const resultWithSymbol = word?.toLowerCase().replace(/[_-]/g, ' ');
  const resultWithoutSymbol = word?.charAt(0)?.toUpperCase() + word?.slice(1);
  const lastResult = resultWithSymbol
    ? resultWithSymbol
        .split(' ')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
    : resultWithoutSymbol;
  return lastResult || '';
};

export const showPaymentDuePrice = (data) => {
  if (data.charges?.balanceDue || data.charges?.balanceDue === 0) {
    return data.charges.balanceDue;
  }
  return data?.paid ? 0 : data.charges?.total;
};
